// =============================================================================
//  SCREEN: TemplateDesignerScreen (Final mit allen Features)
// =============================================================================
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api';
import { toast } from 'react-toastify';
import { Rnd } from 'react-rnd';

const A4_ASPECT_RATIO = 210 / 297;
const PLACEHOLDERS = [
  '[Anrede]', '[Vorname]', '[Nachname]', '[Name (komplett)]', '[Mannschaft]',
  '[Gesamtholz]', '[Platz]', '[Wertung]', '[Datum]', '[Turniername]', '[Ort]'
];
const FONT_FAMILIES = ['helvetica', 'times', 'courier'];
const FONT_WEIGHTS = ['normal', 'bold', 'italic'];
const TEXT_ALIGNS = ['left', 'center', 'right'];
const GRID_SIZE = 10;

const TemplateDesignerScreen = () => {
  const { id } = useParams();
  const [template, setTemplate] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const { data } = await apiClient.get(`/api/templates/${id}`);
        setTemplate(data);
      } catch (err) {
        toast.error("Vorlage konnte nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [id]);

  const handleUpdateElement = (id, newProps) => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => el.id === id ? { ...el, ...newProps } : el)
    }));
  };
  
  const addElement = (type, content) => {
    let newElement = {
      id: `el-${Date.now()}`, type, content, x: 20, y: 20, width: 200, height: 50,
      fontSize: '16', color: '#000000', fontWeight: 'normal', textAlign: 'left', fontFamily: 'helvetica', printable: true,
    };
    if (type === 'image') {
        newElement.width = 100;
        newElement.height = 100;
    }
    if (type === 'line') {
        newElement.height = 2;
    }
    setTemplate(prev => ({ ...prev, elements: [...prev.elements, newElement] }));
    setSelectedElementId(newElement.id);
  };

  const removeElement = () => {
      if (!selectedElementId) return;
      setTemplate(prev => ({
          ...prev,
          elements: prev.elements.filter(el => el.id !== selectedElementId)
      }));
      setSelectedElementId(null);
  };
  
  const handleSave = async () => {
    try {
      const { _id, createdAt, updatedAt, __v, ...templateData } = template;
      await apiClient.put(`/api/templates/${id}`, templateData);
      toast.success("Vorlage gespeichert!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Fehler beim Speichern.");
    }
  };
  
  const handleImageUpload = async (e, isBackground) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('backgroundImage', file);

    try {
      const { data } = await apiClient.post('/api/templates/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (isBackground) {
        setTemplate(prev => ({...prev, backgroundImage: data.path}));
      } else {
        addElement('image', data.path);
      }
      toast.success("Bild hochgeladen!");
    } catch (err) {
      toast.error("Fehler beim Hochladen des Bildes.");
    }
  };
  
  const removeBackgroundImage = () => {
    // We just nullify it in the state. handleSave will persist this.
    setTemplate(prev => ({...prev, backgroundImage: null}));
  };

  const selectedElement = template?.elements.find(el => el.id === selectedElementId);

  if (loading) return <p>Lade Designer...</p>;
  if (!template) return <p>Vorlage nicht gefunden.</p>;

  return (
    <div className="designer-container">
      <div className="designer-toolbar">
        <h3>Werkzeuge</h3>
        <button onClick={() => addElement('text', 'Freitext...')}>Textfeld</button>
        <select onChange={(e) => { addElement('placeholder', e.target.value); e.target.value = ''; }} value="">
            <option value="" disabled>Platzhalter...</option>
            {PLACEHOLDERS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <label className="file-upload-btn">
            Bild...
            <input type="file" onChange={(e) => handleImageUpload(e, false)} accept="image/png, image/jpeg" />
        </label>
        <button onClick={() => addElement('line', '')}>Linie</button>
        <hr />
        <h4>Ansicht</h4>
        <label className="checkbox-item"><input type="checkbox" checked={showGrid} onChange={e => setShowGrid(e.target.checked)} /> Hilfslinien</label>
        <label className="checkbox-item"><input type="checkbox" checked={snapToGrid} onChange={e => setSnapToGrid(e.target.checked)} /> Am Raster ausrichten</label>
        <hr />
        <h4>Hintergrund</h4>
        <label className="file-upload-btn">
            Hintergrund wählen...
            <input type="file" onChange={(e) => handleImageUpload(e, true)} accept="image/png, image/jpeg" />
        </label>
        {template.backgroundImage && <button onClick={removeBackgroundImage}>Hintergrund entfernen</button>}
        <hr />
        <button onClick={handleSave} className="btn-primary-action">Vorlage Speichern</button>
        <Link to="/vorlagen" className="btn-back">Zurück zur Übersicht</Link>
      </div>
      <div className="designer-canvas-wrapper">
        <div 
          className="designer-canvas" 
          ref={canvasRef} 
          style={{ 
            aspectRatio: A4_ASPECT_RATIO,
            backgroundImage: template.backgroundImage ? `url(${template.backgroundImage})` : 'none'
          }}
          onClick={() => setSelectedElementId(null)}
        >
          {showGrid && <div className="grid-overlay"></div>}
          
          {template.elements.map((el) => (
            <Rnd
              key={el.id}
              size={{ width: el.width, height: el.height }}
              position={{ x: el.x, y: el.y }}
              onDragStop={(e, d) => handleUpdateElement(el.id, { x: d.x, y: d.y })}
              onResizeStop={(e, direction, ref, delta, position) => {
                handleUpdateElement(el.id, {
                  width: parseInt(ref.style.width),
                  height: parseInt(ref.style.height),
                  ...position,
                });
              }}
              onClick={(e) => { e.stopPropagation(); setSelectedElementId(el.id); }}
              className={selectedElementId === el.id ? 'selected-element' : ''}
              bounds="parent"
              dragGrid={snapToGrid ? [GRID_SIZE, GRID_SIZE] : [1, 1]}
              resizeGrid={snapToGrid ? [GRID_SIZE, GRID_SIZE] : [1, 1]}
            >
              {el.type === 'line' ? (
                <div className="element-content line-element" style={{ backgroundColor: el.color, height: `${el.height}px` }}></div>
              ) : (
                <div 
                  className="element-content"
                  style={{
                    fontSize: `${el.fontSize}px`, color: el.color,
                    fontWeight: el.fontWeight, textAlign: el.textAlign,
                    fontFamily: el.fontFamily,
                    backgroundImage: el.type === 'image' ? `url(${el.content})` : 'none',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                >
                  {el.type !== 'image' ? el.content : ''}
                </div>
              )}
            </Rnd>
          ))}
        </div>
      </div>
      <div className="designer-properties">
        <h3>Eigenschaften</h3>
        {selectedElement ? (
          <div className="properties-form">
            {selectedElement.type !== 'image' && selectedElement.type !== 'line' && (
                <>
                  <div className="form-group">
                      <label>Text / Inhalt</label>
                      <input type="text" value={selectedElement.content} onChange={e => handleUpdateElement(selectedElement.id, { content: e.target.value })} />
                  </div>
                  <div className="form-group">
                      <label>Schriftgröße (px)</label>
                      <input type="number" value={selectedElement.fontSize} onChange={e => handleUpdateElement(selectedElement.id, { fontSize: e.target.value })} />
                  </div>
                  <div className="form-group">
                      <label>Schriftfarbe</label>
                      <input type="color" value={selectedElement.color} onChange={e => handleUpdateElement(selectedElement.id, { color: e.target.value })} style={{height: '40px'}} />
                  </div>
                  <div className="form-group">
                      <label>Schriftart</label>
                      <select value={selectedElement.fontFamily} onChange={e => handleUpdateElement(selectedElement.id, { fontFamily: e.target.value })}>
                          {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                  </div>
                  <div className="form-group">
                      <label>Schriftschnitt</label>
                      <select value={selectedElement.fontWeight} onChange={e => handleUpdateElement(selectedElement.id, { fontWeight: e.target.value })}>
                          {FONT_WEIGHTS.map(w => <option key={w} value={w}>{w}</option>)}
                      </select>
                  </div>
                  <div className="form-group">
                      <label>Ausrichtung</label>
                      <select value={selectedElement.textAlign} onChange={e => handleUpdateElement(selectedElement.id, { textAlign: e.target.value })}>
                          {TEXT_ALIGNS.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                  </div>
                </>
            )}
            {selectedElement.type === 'line' && (
                <>
                <div className="form-group">
                    <label>Linienfarbe</label>
                    <input type="color" value={selectedElement.color} onChange={e => handleUpdateElement(selectedElement.id, { color: e.target.value })} style={{height: '40px'}} />
                </div>
                <div className="form-group">
                    <label>Linienstärke (px)</label>
                    <input type="number" value={selectedElement.height} onChange={e => handleUpdateElement(selectedElement.id, { height: parseInt(e.target.value) })} />
                </div>
                </>
            )}
            <div className="form-group">
              <label className="checkbox-item">
                <input type="checkbox" checked={selectedElement.printable} onChange={e => handleUpdateElement(selectedElement.id, { printable: e.target.checked })} />
                Element im PDF drucken
              </label>
            </div>
            <button onClick={removeElement} className="btn-delete">Element löschen</button>
          </div>
        ) : (template.backgroundImage && 
            <div className="properties-form">
                <p>Hintergrundbild ausgewählt. Es hat keine weiteren Eigenschaften.</p>
            </div>
        )}
        {!selectedElement && !template.backgroundImage && (
            <p>Ein Element auswählen, um dessen Eigenschaften zu bearbeiten.</p>
        )}
      </div>
    </div>
  );
};

export default TemplateDesignerScreen;